import { useEffect, useState } from "react";
import { Loader2, MapPin, CheckCircle2, Gift, Lock, AlertCircle, Pencil } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO = "https://static.prod-images.emergentagent.com/jobs/1bbf805d-c799-41a3-98b1-da09b291e67c/images/4e2fe56637b8e6aa552124ef0e905ee968adb2e1bcc291a69c5e97d3364cbfb4.jpeg";
const EMPTY = { name: "", mobile: "", alt_mobile: "", address: "", landmark: "", pincode: "", city: "", state: "", website: "" };
const MOBILE_RE = /^[6-9]\d{9}$/;
const PIN_RE = /^[1-9]\d{5}$/;
const SERIF = { fontFamily: "'Fraunces', Georgia, serif" };
const inputCls = "h-12 rounded-xl border-[#E6D9BF] bg-white text-[15px] text-[#1B2A4A] placeholder:text-[#9AA3B5] focus-visible:ring-2 focus-visible:ring-[#D4A017]/50 focus-visible:border-[#D4A017] transition-[border-color,box-shadow]";

const validate = (f) => {
  if (f.name.trim().length < 2) return "Please enter your full name.";
  if (!MOBILE_RE.test(f.mobile)) return "Please enter a valid 10-digit mobile number.";
  if (f.alt_mobile && !MOBILE_RE.test(f.alt_mobile)) return "Please check the alternate mobile number, or leave it blank.";
  if (f.alt_mobile && f.alt_mobile === f.mobile) return "The alternate number should be different from your main number.";
  if (f.address.trim().length < 5) return "Please enter your full delivery address.";
  if (!PIN_RE.test(f.pincode)) return "Please enter a valid 6-digit pin code.";
  if (!f.city.trim() || !f.state.trim()) return "Please enter your city and state.";
  return "";
};

const useFestiveFont = () => useEffect(() => {
  if (document.getElementById("fraunces-font")) return;
  const link = document.createElement("link");
  link.id = "fraunces-font";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap";
  document.head.appendChild(link);
}, []);

const Field = ({ label, optional, children, hint }) => (
  <label className="block">
    <span className="flex items-baseline justify-between mb-1.5">
      <span className="text-[13px] font-semibold text-[#1B2A4A]">{label}</span>
      {optional && <span className="text-[11px] text-[#8A93A6]">Optional</span>}
    </span>
    {children}
    {hint}
  </label>
);

const PhoneInput = ({ value, onChange, testId }) => (
  <span className="relative block">
    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-[#6B7489]">+91</span>
    <Input value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric"
      autoComplete="tel-national" data-testid={testId} className={`${inputCls} pl-14`} />
  </span>
);

const PinHint = ({ state, city, region }) => {
  if (state === "loading") return <span className="mt-1.5 flex items-center gap-1.5 text-xs text-[#6B7489]" data-testid="gift-pin-status"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding your city…</span>;
  if (state === "found") return <span className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid="gift-pin-status"><MapPin className="h-3.5 w-3.5" /> {city}, {region}</span>;
  if (state === "notfound") return <span className="mt-1.5 block text-xs text-[#A87608]" data-testid="gift-pin-status">We couldn't find this pin code. Please type your city and state below.</span>;
  return null;
};

const Entity = ({ tagline, testId, children }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="flex h-10 items-center sm:h-11">{children}</div>
    <p className="mt-1.5 text-[11px] leading-snug text-white/75 sm:text-xs">{tagline}</p>
  </div>
);

const Brand = () => (
  <div className="relative mx-auto max-w-2xl px-5 pt-6 animate-in fade-in duration-700" data-testid="gift-brand">
    <div className="flex items-stretch gap-4 sm:gap-6">
      <Entity tagline="Your Wealth Management Partner" testId="gift-entity-shree-finserv">
        <img src="/shree-finserv-logo-on-dark.png" alt="Shree Finserv" className="h-10 w-auto sm:h-11" />
      </Entity>
      <span className="w-px shrink-0 bg-white/25" aria-hidden="true" />
      <Entity tagline="Health and Term Insurance Advisory" testId="gift-entity-kavach-box">
        <img src="/kavachbox-logo-on-dark-cropped.png" alt="Kavach Box" className="h-6 w-auto sm:h-7" data-testid="gift-logo" />
      </Entity>
    </div>
  </div>
);

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0B1730] text-white" data-testid="diwali-gift-page">
    <div className="relative h-[340px] sm:h-[420px] overflow-hidden">
      <img src={HERO} alt="Glowing diyas, marigolds and a gift box" className="absolute inset-0 h-full w-full object-cover object-[center_70%] animate-in fade-in zoom-in-105 duration-1000" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1730]/80 via-[#0B1730]/10 to-[#0B1730]" />
      <Brand />
    </div>
    <main className="relative mx-auto -mt-32 max-w-2xl px-5 pb-16 sm:-mt-40">{children}</main>
  </div>
);

const Message = ({ icon: Icon, title, children, testId }) => (
  <div className="rounded-3xl bg-[#FFFBF3] p-8 text-center text-[#1B2A4A] shadow-2xl shadow-black/40 animate-in fade-in slide-in-from-bottom-4 duration-700 sm:p-12" data-testid={testId}>
    <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#D4A017]/15">
      <Icon className="h-8 w-8 text-[#A87608]" />
    </span>
    <h1 className="text-3xl sm:text-4xl" style={SERIF}>{title}</h1>
    <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-[#3A4557]">{children}</div>
  </div>
);

const SuccessView = ({ name, updated, onEdit }) => (
  <Message icon={CheckCircle2} title={`Thank you, ${name.trim().split(" ")[0]}!`} testId="gift-success">
    <p data-testid="gift-success-text">{updated ? "We've updated your delivery address." : "Your delivery address is saved."} Your Diwali gift will be on its way soon.</p>
    <p>Wishing you and your family a bright, joyful and safe Diwali.</p>
    <p className="pt-2 text-sm font-semibold text-[#A87608]">Shree Finserv and the Kavach Box family</p>
    <button type="button" onClick={onEdit} data-testid="gift-edit-again-btn" className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#E6D9BF] px-5 py-2.5 text-sm font-medium text-[#1B2A4A] transition-colors hover:bg-[#F5EBD6]">
      <Pencil className="h-4 w-4" /> Change my details
    </button>
  </Message>
);

const Intro = () => (
  <div className="mb-8 animate-in fade-in slide-in-from-bottom-3 duration-700">
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#E8B830]">Shubh Deepavali</p>
    <h1 className="text-4xl leading-[1.1] text-white sm:text-5xl" style={SERIF}>A little Diwali sweetness is coming your way</h1>
    <p className="mt-4 max-w-lg text-base text-white/75">Shree Finserv and the Kavach Box family would love to send you a Diwali gift. Just tell us where to deliver it.</p>
  </div>
);

export default function DiwaliGift() {
  useFestiveFont();
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(null);
  const [pinState, setPinState] = useState("idle");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    fetch(`${API}/api/gift/diwali/status`).then((r) => r.json()).then((d) => setOpen(d.open !== false)).catch(() => setOpen(true));
  }, []);

  useEffect(() => {
    if (!PIN_RE.test(form.pincode)) { setPinState("idle"); return undefined; }
    let active = true;
    setPinState("loading");
    fetch(`${API}/api/gift/pincode/${form.pincode}`).then((r) => r.json()).then((d) => {
      if (!active) return;
      if (d.found) { setForm((f) => ({ ...f, city: d.city, state: d.state })); setPinState("found"); }
      else setPinState("notfound");
    }).catch(() => active && setPinState("notfound"));
    return () => { active = false; };
  }, [form.pincode]);

  const submit = async (e) => {
    e.preventDefault();
    const msg = validate(form);
    setError(msg);
    if (msg) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/gift/diwali/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setDone({ updated: data.updated }); window.scrollTo({ top: 0, behavior: "smooth" }); }
      else if (res.status === 403) setOpen(false);
      else setError(typeof data.detail === "string" ? data.detail : "Something went wrong. Please try again.");
    } catch { setError("Network error. Please check your connection and try again."); }
    setSubmitting(false);
  };

  if (open === null) return <Shell><div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#E8B830]" /></div></Shell>;
  if (!open) return (
    <Shell>
      <Message icon={Gift} title="This form is now closed" testId="gift-closed">
        <p>Thank you for stopping by. We're no longer collecting addresses for Diwali gifts.</p>
        <p>If you have a question, please message Prashant directly.</p>
      </Message>
    </Shell>
  );
  if (done) return <Shell><SuccessView name={form.name} updated={done.updated} onEdit={() => setDone(null)} /></Shell>;

  return (
    <Shell>
      <Intro />
      <form onSubmit={submit} noValidate data-testid="gift-form" className="rounded-3xl bg-[#FFFBF3] p-6 text-[#1B2A4A] shadow-2xl shadow-black/40 animate-in fade-in slide-in-from-bottom-6 duration-700 sm:p-10">
        <p className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#A87608]">Your details</p>
        <div className="space-y-5">
          <Field label="Full name">
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} autoComplete="name" data-testid="gift-name-input" className={inputCls} />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Mobile number"><PhoneInput value={form.mobile} onChange={set("mobile")} testId="gift-mobile-input" /></Field>
            <Field label="Alternate mobile" optional><PhoneInput value={form.alt_mobile} onChange={set("alt_mobile")} testId="gift-alt-mobile-input" /></Field>
          </div>
        </div>

        <p className="mb-5 mt-10 text-xs font-bold uppercase tracking-[0.2em] text-[#A87608]">Where should we deliver?</p>
        <div className="space-y-5">
          <Field label="Full address">
            <Textarea value={form.address} onChange={(e) => set("address")(e.target.value)} rows={3} autoComplete="street-address" data-testid="gift-address-input" className={`${inputCls} h-auto min-h-[96px] py-3`} />
          </Field>
          <Field label="Nearby landmark" optional>
            <Input value={form.landmark} onChange={(e) => set("landmark")(e.target.value)} data-testid="gift-landmark-input" className={inputCls} />
          </Field>
          <Field label="Pin code" hint={<PinHint state={pinState} city={form.city} region={form.state} />}>
            <Input value={form.pincode} onChange={(e) => set("pincode")(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="postal-code" data-testid="gift-pincode-input" className={`${inputCls} sm:max-w-[220px]`} />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="City"><Input value={form.city} onChange={(e) => set("city")(e.target.value)} autoComplete="address-level2" data-testid="gift-city-input" className={inputCls} /></Field>
            <Field label="State"><Input value={form.state} onChange={(e) => set("state")(e.target.value)} autoComplete="address-level1" data-testid="gift-state-input" className={inputCls} /></Field>
          </div>
        </div>

        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" aria-hidden="true" />

        {error && (
          <p className="mt-6 flex items-start gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="gift-form-error">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </p>
        )}

        <button type="submit" disabled={submitting} data-testid="gift-submit-btn" className="mt-8 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#D4A017] text-base font-bold text-[#0B1730] shadow-lg shadow-[#D4A017]/30 transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#E8B830] active:translate-y-0 disabled:opacity-70">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Gift className="h-5 w-5" />}
          {submitting ? "Saving…" : "Send me my Diwali gift"}
        </button>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-[#6B7489]">
          <Lock className="h-3.5 w-3.5" /> We'll only use these details to deliver your gift.
        </p>
      </form>
    </Shell>
  );
}
